/**
 * Ember · 星火 管理后台 — 最小前端可观测性
 *
 * 目标(HARDENING_ROADMAP 批6-h / 闭合 DISC-014 前端侧):
 * - 统一收口 window.onerror / window.onunhandledrejection 与 React ErrorBoundary 捕获的异常
 * - 结构化 console 输出, 便于本地与线上 vConsole / DevTools 排查(本地兜底)
 * - 远端上报: fire-and-forget POST 到后端 `/api/v1/client-errors`(免登录, 202),
 *   失败自吞且不递归, base URL 复用 api.ts 的 `getApiBase()`(不硬编码地址)
 *
 * 注意: 本模块不在前端存敏感数据, 不上报 token; 仅上报错误信息与 request_id(后端错误体已带)。
 */
import { apiErrorBody, getApiBase } from '../services/api';
import { ADMIN_BUILD_INFO } from '../../.generated/build-info';

/** 后端 POST /api/v1/client-errors 路径（免登录, 返回 202）。 */
const CLIENT_ERRORS_PATH = '/api/v1/client-errors';

/**
 * 后端 ClientErrorReport 字段长度上限（对齐 apps/api/ember/client_errors/schemas.py）。
 * 超长会被后端 422 拒收, 这里先截断, 保证常规上报不被 validation 拦掉。
 */
const FIELD_LIMITS = {
  message: 500,
  requestId: 128,
  componentStack: 4000,
  stack: 4000,
  url: 500,
} as const;

function truncate(value: string, max: number): string {
  return value.length > max ? value.slice(0, max) : value;
}

function truncateOptional(value: string | null, max: number): string | null {
  if (value == null) {
    return null;
  }
  const trimmed = truncate(value, max);
  return trimmed.length > 0 ? trimmed : null;
}

export type ClientErrorSource =
  | 'window.onerror'
  | 'window.onunhandledrejection'
  | 'react.error-boundary';

export type ClientErrorReport = {
  source: ClientErrorSource;
  message: string;
  /** 后端错误体携带的 request_id, 便于跨前后端关联日志 */
  requestId: string | null;
  /** 渲染异常时的 React 组件栈 */
  componentStack: string | null;
  stack: string | null;
  url: string;
  occurredAt: string;
  appVersion: string;
  gitSha: string;
};

/**
 * 从任意 error 中尽力提取后端 request_id。
 * 后端错误体形如 { error: {...}, request_id: 'req_xxx' }(见 services/api.ts apiErrorBody)。
 */
export function extractRequestId(error: unknown): string | null {
  const body = apiErrorBody(error);
  const direct = body.request_id ?? body.requestId;
  if (typeof direct === 'string' && direct.length > 0) {
    return direct;
  }
  const nestedError = body.error;
  if (nestedError && typeof nestedError === 'object' && !Array.isArray(nestedError)) {
    const nested = (nestedError as Record<string, unknown>).request_id;
    if (typeof nested === 'string' && nested.length > 0) {
      return nested;
    }
  }
  return null;
}

function errorMessage(error: unknown): string {
  if (typeof error === 'string') {
    return error;
  }
  if (error instanceof Error) {
    return error.message;
  }
  if (error && typeof error === 'object') {
    const candidate = (error as { message?: unknown }).message;
    if (typeof candidate === 'string') {
      return candidate;
    }
  }
  return String(error ?? '未知错误');
}

function errorStack(error: unknown): string | null {
  if (error instanceof Error && typeof error.stack === 'string') {
    return error.stack;
  }
  return null;
}

/**
 * 把内部 camelCase 报告映射为后端 ClientErrorReport 蛇形契约, 并按上限截断。
 * message 必填; 兜底为非空, 避免后端 min_length=1 校验失败。
 */
function toRemotePayload(report: ClientErrorReport): {
  message: string;
  request_id: string | null;
  source: ClientErrorSource;
  platform: 'admin';
  app_version: string;
  git_sha: string;
  component_stack: string | null;
  stack: string | null;
  url: string | null;
  occurred_at: string | null;
} {
  const message = report.message && report.message.length > 0 ? report.message : '未知错误';
  return {
    message: truncate(message, FIELD_LIMITS.message),
    request_id: truncateOptional(report.requestId, FIELD_LIMITS.requestId),
    source: report.source,
    platform: 'admin',
    app_version: truncate(report.appVersion, 64),
    git_sha: truncate(report.gitSha, 40),
    component_stack: truncateOptional(report.componentStack, FIELD_LIMITS.componentStack),
    stack: truncateOptional(report.stack, FIELD_LIMITS.stack),
    url: truncateOptional(report.url.length > 0 ? report.url : null, FIELD_LIMITS.url),
    occurred_at: report.occurredAt.length > 0 ? report.occurredAt : null,
  };
}

/**
 * 远端上报。POST 到后端 `/api/v1/client-errors`(免登录, 返回 202)。
 *
 * 三条纪律:
 * 1. **fire-and-forget**: 不返回 / 不被 await, 不阻塞 UI 与调用方; 失败也不影响渲染。
 * 2. **吞掉自身失败**: `.catch` 内只 `console.warn`, 绝不再调用 `reportClientError` /
 *    `dispatchToRemote` —— 否则上报失败会反复触发上报形成递归循环。原生 fetch 的 reject
 *    已被这里捕获, 也不会冒泡成 `unhandledrejection` 再被全局 handler 二次上报。
 * 3. **本地兜底保留**: 结构化 console 由调用方 `reportClientError` 负责, 与远端上报并行,
 *    远端不可用时本地日志仍在。
 */
function dispatchToRemote(report: ClientErrorReport): void {
  if (typeof fetch !== 'function') {
    return;
  }

  const base = getApiBase();
  const url = `${base}${CLIENT_ERRORS_PATH}`;
  const payload = toRemotePayload(report);

  // 不 await: 调用方 reportClientError 立即返回, 上报在后台完成。
  void fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    // 免登录端点, 不带 credentials, 不上报 token。
    credentials: 'omit',
    // 页面卸载时仍尽力把上报送达, 不影响导航。
    keepalive: true,
  }).catch((dispatchError: unknown) => {
    // 防递归: 此处只能写本地日志, 绝不可再触发任何上报路径。
    console.warn('[ember-admin][client-error] remote dispatch failed', dispatchError);
  });
}

/**
 * 统一上报入口。ErrorBoundary 与全局 handler 都调用本函数。
 */
export function reportClientError(input: {
  source: ClientErrorSource;
  error: unknown;
  componentStack?: string | null;
}): ClientErrorReport {
  const report: ClientErrorReport = {
    source: input.source,
    message: errorMessage(input.error),
    requestId: extractRequestId(input.error),
    componentStack: input.componentStack ?? null,
    stack: errorStack(input.error),
    url: typeof window !== 'undefined' ? window.location.href : '',
    occurredAt: new Date().toISOString(),
    appVersion: ADMIN_BUILD_INFO.packageVersion,
    gitSha: ADMIN_BUILD_INFO.gitSha,
  };

  // 结构化 console: 一行可被 DevTools / 日志采集器解析。
  console.error('[ember-admin][client-error]', report);

  dispatchToRemote(report);
  return report;
}

let globalHandlersInstalled = false;

/**
 * 安装全局未捕获异常 handler。应在应用入口调用一次。
 */
export function installGlobalErrorHandlers(): void {
  if (globalHandlersInstalled || typeof window === 'undefined') {
    return;
  }
  globalHandlersInstalled = true;

  window.addEventListener('error', (event: ErrorEvent) => {
    reportClientError({
      source: 'window.onerror',
      error: event.error ?? event.message,
    });
  });

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    reportClientError({
      source: 'window.onunhandledrejection',
      error: event.reason,
    });
  });
}
